import React, { useState, useRef, useEffect } from "react";
import _ from 'lodash'
import { useDispatch, useSelector } from "react-redux";

// Inputs
import { DropDownField, TextInputField, CalendarField, MultiSelectField } from "../../../helpers";
import Button from "../../../helpers/Button";
import { Menu } from 'primereact/menu';
import { utils, write } from "xlsx";
import Papa from "papaparse";
import Loading from "../../../common/Loading";

// utils
import { getDateString, getProductName, getProductSpecification, getCompanyShortName, numberConversion, round, isValidNumber, getFullName } from '../../../utils';

// redux
import { fetchStockReport, updatePaginationParams, updateSearchOptions, updateSearchParams, updateStockReportData, setSearchConfig } from "../../../services/redux/reducers/stock/stockReport";
import { raiseToast } from "../../../services/redux/reducers/toast/toast";

// api
import { getBuyerPOStockReport, getDiscardedStockReport, getStockVarianceReport, searchStockMoves } from "../../../services/api/stockMoves";
import { searchStockWarehouse, getStockMovementReport, getStockAgingReport, getRejectedStockReport, getStockConsumptionStockReport } from "../../../services/api/stockWarehouse";
import { searchStockDoc } from "../../../services/api/stockDoc";

import { getPMCompanyConfig } from "../../../setup/pmCompanyConfig";


const initialSearchConfig = [
    {
        srNo: 1,
        static: true,
        type: "SELECT",
        name: "dataType",
        label: "Data type",
        options: [
            { label: 'Stock Doc', value: 'stockDoc' },
            { label: 'Stock Warehouse', value: 'stockWarehouse' },
            { label: 'Stock Movement', value: 'stockMovement' },
            { label: 'Stock Aging Report', value: 'stockAging' },
            { label: 'Stock Variance', value: 'stockVariance' },
            { label: 'Buyer PO Stock', value: 'buyerPOStock' },
            { label: 'Discarded/Scrap Stock', value: 'discardedStock' },
            { label: 'All Stock Moves', value: 'allStockMoves' },
            { label: 'Rejected Stock', value: 'rejectedStock' },
            { label: 'Stock Consumption', value: 'stockConsumption' }
        ]
    },
    {
        srNo: 2,
        static: true,
        type: "SEARCH_BY",
        name: "searchBy",
        label: "Search by",
        options: [
            { label: 'No Filters', value: 'all' },
            { label: 'Material Id', value: 'materialId' },
            { label: 'Doc No', value: 'docNo' },
            { label: 'Material Details', value: 'materialDetails' },
            // { label: 'Product Name', value: 'productName' },
            // { label: 'Product Specification', value: 'productSpecification' },
            { label: 'KAM Name', value: 'KAM' },
            // { label: 'PM Branch', value: 'PMBranch' }
        ]
    }
]

let customStockMovesSearchConfig = [
    {
        srNo: 3,
        static: true,
        type: "SELECT",
        name: "docType",
        label: "Doc Type",
        options: [
            { label: 'Seller', value: '15', dataType: 'stockDoc' },
            { label: 'Buyer', value: '14', dataType: 'stockDoc' }
        ],
        multiSelectOptions: true
    },
    {
        srNo: 4,
        static: true,
        type: "SELECT",
        name: "stockMoveType",
        label: "Stock Move Type",
        multiSelectOptions: true
    },
    {
        srNo: 5,
        static: false,
        type: "SELECT",
        name: "warehouseId",
        label: "Warehouse Name",
    },
    {
        srNo: 6,
        static: true,
        type: "DATE",
        name: "dateType",
        label: "Date",
        options: [
            // { label: 'Stock Move Created Date', value: 'createdAt', dataType: 'allStockMoves' },
            { label: 'Stock Move Date', value: 'moveDate', dataType: 'allStockMoves' },
            { label: 'Stock Move Date', value: 'stockMoveDate', dataType: 'stockMovement' },
            { label: 'Report Date', value: 'reportDate', dataType: 'stockWarehouse' },
            { label: 'Report Date', value: 'reportDate', dataType: 'discardedStock' },
            { label: 'Rejected Date', value: 'rejectedDate', dataType: 'rejectedStock' },
            { label: 'Seller PO Date', value: 'sellerPODate', dataType: 'stockConsumption' },
            { label: 'Report Date', value: 'reportDate', dataType: 'stockAging' },
        ]
    }
]

const defaultStockSearchParams = {
    searchBy: "all",
    searchText: "",
    docType: []
}

const Search = (props) => {
    const dispatch = useDispatch()

    const { handleTableColumn, companyWarehouses, statusList, materialBrands, stockMoveTypes } = props

    const [isLoading, setIsLoading] = useState(false)
    const [searchConfig, setSearchConfig] = useState([...initialSearchConfig])

    const isPageLoading = useSelector(state => state.stockReport.loading)
    const paginationParams = useSelector(state => state.stockReport.paginationParams)
    const searchParams = useSelector(state => state.stockReport.searchParams)
    const searchOptions = useSelector(state => state.stockReport.searchOptions)

    const userDetails = useSelector(state => state.userDetails)

    const pmCompanyConfig = getPMCompanyConfig(userDetails.PMCompanyDetails.companyId)
    let { numberLocale } = pmCompanyConfig

    const filterMenu = useRef(null)
    const downloadMenu = useRef(null)

    const maxDateCondForCalender = new Date()

    useEffect(() => {
        setSearchConfig([...initialSearchConfig])
        if (searchOptions.length === 0) {
            let filteredSearchOptions = searchConfig.filter(eachOption => eachOption.static)
            filteredSearchOptions = JSON.parse(JSON.stringify(filteredSearchOptions))
            const docTypeFilter = filteredSearchOptions.find(eachSearchOpt => eachSearchOpt.type === "SELECT" && eachSearchOpt.name === "docType")
            if(!docTypeFilter){
                const docTypeConfig = customStockMovesSearchConfig.find(eachCustomConfig => eachCustomConfig.name === "docType")
                filteredSearchOptions = [...filteredSearchOptions, docTypeConfig]
            }

            filteredSearchOptions.forEach(eachSearchOpt => {
                if(eachSearchOpt.type === "SEARCH_BY"){
                    const filteredOptions = eachSearchOpt.options.filter(eachSearchByOpt =>{ 
                        let relatedDataTypeSearchByOptions = ['all', 'materialId', 'docNo', 'materialDetails'] //update here for every data type change initially!
                        return relatedDataTypeSearchByOptions.includes(eachSearchByOpt.value)
                })
                    eachSearchOpt.options = filteredOptions
                }
            })
            dispatch(updateSearchOptions(filteredSearchOptions))
        }
    }, [])

    useEffect(() => {
        if (searchParams) {
            if (searchParams?.dataType === "stockDoc") {

                let updatedSearchParams = { ...defaultStockSearchParams, dataType: searchParams.dataType }
                if (updatedSearchParams.fromDate) {
                    delete updateSearchParams.fromDate
                }

                if (updatedSearchParams.toDate) {
                    delete updateSearchParams.toDate
                }

                if (updatedSearchParams?.dateType) {
                    delete updateSearchParams.dateType
                }

                dispatch(updateSearchParams(updatedSearchParams))

                if (searchOptions.length != 0) {
                    let relatedSearchOptions = searchOptions?.filter(eachOption => eachOption?.name !== "warehouseId" && eachOption.name != "dateType" && eachOption.name !== "stockMoveType")
                    let docTypeSearchOption = searchOptions?.find(eachOption => eachOption.name === "docType")
                    if (!docTypeSearchOption) {
                        let relatedCustomSearchConfig = customStockMovesSearchConfig?.find(eachOption => eachOption.name === "docType")
                        if (relatedCustomSearchConfig) {
                            relatedSearchOptions.push(relatedCustomSearchConfig)
                        }
                    }
                    let updatedSearchOptions = relatedSearchOptions?.map(eachOption => {
                        if (eachOption?.name === "searchBy") {
                            let relatedSearchByOptions = [
                                { label: 'No Filters', value: 'all' },
                                { label: 'Material Id', value: 'materialId' },
                                { label: 'Material Details', value: 'materialDetails' },
                                { label: 'Doc No', value: 'docNo' },
                            ]
                            return { ...eachOption, options: relatedSearchByOptions }
                        }
                        return eachOption
                    })
                    
                    let relatedSearchConfig = searchConfig?.filter(eachOption => eachOption.name != "warehouseId" && eachOption.name != "dateType")

                    if (relatedSearchConfig.length > 0) {
                        setSearchConfig([...relatedSearchConfig])
                    }
                    dispatch(updateSearchOptions(updatedSearchOptions))
                }
            } 
            else if (searchParams?.dataType === "stockWarehouse") {

                if (searchOptions.length != 0) {

                    let updatedSearchOptions = [...searchOptions]
                    updatedSearchOptions = updatedSearchOptions.filter(eachSearchOpt => eachSearchOpt.name !== "docType" && eachSearchOpt.name != "stockMoveType" && eachSearchOpt.name !== "warehouseId")
                    let dateTypeSearchOption = searchOptions?.find(eachOption => eachOption.name === "dateType")
                    if (!dateTypeSearchOption) {
                        let relatedCustomSearchConfig = customStockMovesSearchConfig?.find(eachOption => eachOption.name === "dateType")
                        if (relatedCustomSearchConfig) {
                            updatedSearchOptions.push(relatedCustomSearchConfig)
                        }
                    }

                    updatedSearchOptions = updatedSearchOptions?.map(eachOption => {
                        if (eachOption?.name === "searchBy") {
                            let relatedSearchByOptions = [
                                { label: 'No Filters', value: 'all' },
                                { label: 'Material Id', value: 'materialId' },
                                { label: 'Material Details', value: 'materialDetails' }
                            ]
                            return { ...eachOption, options: relatedSearchByOptions }
                        }
                        return eachOption
                    })

                    let updatedSearchConfig = [...searchConfig]
                    const relatedSearchConfig = searchConfig?.find(eachOption => eachOption.name === "dateType")
                    if (!relatedSearchConfig) {
                        let relatedCustomSearchConfig = customStockMovesSearchConfig?.find(eachOption => eachOption.name === "dateType")
                        if (relatedCustomSearchConfig) {
                            updatedSearchConfig.push(relatedCustomSearchConfig)
                        }
                    }

                    let warehouseSearchConfig = searchConfig?.find(eachOption => eachOption.name === "warehouseId")
                    if (!warehouseSearchConfig) {
                        let relatedCustomSearchConfig = customStockMovesSearchConfig?.find(eachOption => eachOption.name === "warehouseId")
                        if (relatedCustomSearchConfig) {
                            updatedSearchConfig.push(relatedCustomSearchConfig)
                        }
                    } 

                    let updatedSearchParams = { ...defaultStockSearchParams, dataType: searchParams.dataType }

                    let fromDate = new Date()
                    fromDate.setDate(fromDate.getDate())
                    fromDate.setHours(0, 0, 0, 0)
                    fromDate = fromDate.toISOString()

                    updatedSearchParams.fromDate = fromDate
                    updatedSearchParams.dateType = 'reportDate'

                    dispatch(updateSearchParams(updatedSearchParams))
                    dispatch(updateSearchOptions([...updatedSearchOptions]))
                    setSearchConfig(updatedSearchConfig)
                }
            } 
            else if (searchParams?.dataType === "allStockMoves") {
                if (searchOptions.length != 0) {
                    let relatedSearchOptions = searchOptions?.filter(eachOption => eachOption?.name !== "warehouseId")
                    relatedSearchOptions = relatedSearchOptions.filter(eachSearchOpt => eachSearchOpt.name !== "docType" && eachSearchOpt.name !== "warehouseId")
                    let dateTypeSearchOption = searchOptions?.find(eachOption => eachOption.name === "dateType")

                    let updatedSearchOptions = [...relatedSearchOptions]
                    if (!dateTypeSearchOption) {
                        let relatedCustomSearchConfig = customStockMovesSearchConfig?.find(eachOption => eachOption.name === "dateType")
                        if (relatedCustomSearchConfig) {
                            updatedSearchOptions.push(relatedCustomSearchConfig)
                        }
                    }

                    let stockMoveTypeSearchOption = updatedSearchOptions?.find(eachOption => eachOption.name === "stockMoveType")
                    if (!stockMoveTypeSearchOption) {
                        let relatedCustomSearchConfig = customStockMovesSearchConfig?.find(eachOption => eachOption.name === "stockMoveType")
                        if (relatedCustomSearchConfig) {
                            updatedSearchOptions.push(relatedCustomSearchConfig)
                        }
                    }

                    updatedSearchOptions = updatedSearchOptions?.map(eachOption => {
                        if (eachOption?.name === "searchBy") {
                            let relatedSearchByOptions = [
                                { label: 'No Filters', value: 'all' },
                                { label: 'Material Id', value: 'materialId' },
                                { label: 'Material Details', value: 'materialDetails' }
                            ]
                            return { ...eachOption, options: relatedSearchByOptions }
                        }
                        return eachOption
                    })

                    let updatedSearchConfig = [...searchConfig]

                    let relatedSearchConfig = searchConfig?.find(eachOption => eachOption.name === "dateType")
                    if (!relatedSearchConfig) {
                        let relatedCustomSearchConfig = customStockMovesSearchConfig?.find(eachOption => eachOption.name === "dateType")
                        if (relatedCustomSearchConfig) {
                            updatedSearchConfig.push(relatedCustomSearchConfig)
                        }
                    }

                    let relatedWareHouseSearchConfig = searchConfig?.find(eachOption => eachOption.name === "warehouseId")
                    if (!relatedWareHouseSearchConfig) {
                        let relatedCustomSearchConfig = customStockMovesSearchConfig?.find(eachOption => eachOption.name === "warehouseId")
                        if (relatedCustomSearchConfig) {
                            updatedSearchConfig.push(relatedCustomSearchConfig)
                        }
                    }

                    let updatedSearchParams = { ...defaultStockSearchParams, dataType: searchParams.dataType }

                    let toDate = new Date()
                    toDate.setHours(23, 59, 0, 0)
                    toDate = toDate.toISOString()

                    let fromDate = new Date()
                    fromDate.setDate(fromDate.getDate() - 30)
                    fromDate.setHours(0, 0, 0, 0)
                    fromDate = fromDate.toISOString()

                    updatedSearchParams.fromDate = fromDate
                    updatedSearchParams.toDate = toDate
                    updatedSearchParams.dateType = 'moveDate'

                    setSearchConfig(updatedSearchConfig)
                    dispatch(updateSearchOptions([...updatedSearchOptions]))
                    dispatch(updateSearchParams(updatedSearchParams))
                }

            }
            else if (searchParams?.dataType === "stockMovement") {

                let updatedSearchParams = { ...defaultStockSearchParams, dataType: searchParams.dataType }
                if (updatedSearchParams.fromDate) {
                    delete updateSearchParams.fromDate
                }

                if (updatedSearchParams.toDate) {
                    delete updateSearchParams.toDate
                }

                if (updatedSearchParams.dateType) {
                    delete updateSearchParams.dateType
                }

                dispatch(updateSearchParams(updatedSearchParams))
                if (searchOptions.length != 0) {
                    let relatedSearchOptions = searchConfig?.filter(eachOption => eachOption?.name !== "warehouseId")
                    relatedSearchOptions = relatedSearchOptions.filter(eachSearchOpt => eachSearchOpt.name !== "docType" && eachSearchOpt.name != "stockMoveType")
                    let dateTypeSearchOption = searchConfig?.find(eachOption => eachOption.name === "dateType")

                    let updatedSearchOptions = relatedSearchOptions?.map(eachOption => {
                        if (eachOption?.name === "searchBy") {
                            let relatedSearchByOptions = [
                                { label: 'No Filters', value: 'all' },
                                { label: 'Material Id', value: 'materialId' },
                                { label: 'Material Details', value: 'materialDetails' }
                            ]
                            return { ...eachOption, options: relatedSearchByOptions }
                        }
                        return eachOption
                    })
                    if (!dateTypeSearchOption) {
                        let relatedCustomSearchConfig = customStockMovesSearchConfig?.find(eachOption => eachOption.name === "dateType")
                        if (relatedCustomSearchConfig) {
                            updatedSearchOptions.push(relatedCustomSearchConfig)
                        }
                    }


                    let updatedSearchConfig = [...searchConfig]

                    let relatedSearchConfig = searchConfig?.find(eachOption => eachOption.name === "dateType")
                    if (!relatedSearchConfig) {
                        let relatedCustomSearchConfig = customStockMovesSearchConfig?.find(eachOption => eachOption.name === "dateType")
                        if (relatedCustomSearchConfig) {
                            updatedSearchConfig.push(relatedCustomSearchConfig)
                        }
                    }

                    let relatedWareHouseSearchConfig = searchConfig?.find(eachOption => eachOption.name === "warehouseId")
                    if (!relatedWareHouseSearchConfig) {
                        let relatedCustomSearchConfig = customStockMovesSearchConfig?.find(eachOption => eachOption.name === "warehouseId")
                        if (relatedCustomSearchConfig) {
                            updatedSearchConfig.push(relatedCustomSearchConfig)
                        }
                    }

                    let updatedSearchParams = { ...defaultStockSearchParams, dataType: searchParams.dataType }

                    let toDate = new Date()
                    toDate.setDate(toDate.getDate() - 1)
                    toDate.setHours(23, 59, 0, 0)
                    toDate = toDate.toISOString()

                    let fromDate = new Date()
                    fromDate.setDate(1)
                    fromDate.setHours(0, 0, 0, 0)
                    fromDate = fromDate.toISOString()

                    updatedSearchParams.fromDate = fromDate
                    updatedSearchParams.toDate = toDate
                    updatedSearchParams.dateType = 'stockMoveDate'

                    setSearchConfig(updatedSearchConfig)
                    dispatch(updateSearchOptions([...updatedSearchOptions]))
                    dispatch(updateSearchParams(updatedSearchParams))
                }
            }
            else if (searchParams?.dataType === "stockVariance"){
                let updatedSearchParams = { ...defaultStockSearchParams, dataType: searchParams.dataType }
                if (updatedSearchParams.fromDate) {
                    delete updateSearchParams.fromDate
                }

                if (updatedSearchParams.toDate) {
                    delete updateSearchParams.toDate
                }

                if (updatedSearchParams.dateType) {
                    delete updateSearchParams.dateType
                }

                dispatch(updateSearchParams(updatedSearchParams))

                if (searchOptions.length != 0) {
                    //let relatedSearchConfig = customStockMovesSearchConfig?.find(eachOption => eachOption.name === "warehouseId")
                    let relatedSearchOptions = searchOptions?.filter(eachOption => eachOption.name != "dateType")
                    relatedSearchOptions = relatedSearchOptions.filter(eachSearchOpt => eachSearchOpt.name !== "docType" && eachSearchOpt.name != "stockMoveType" && eachSearchOpt.name !== "warehouseId")
                    let updatedSearchOptions = relatedSearchOptions?.map(eachOption => {
                        if (eachOption?.name === "searchBy") {
                            let updatedOptions = [{ label: 'No Filters', value: 'all' },
                                { label: 'Material Id', value: 'materialId' },
                                { label: 'Material Details', value: 'materialDetails' },
                                { label: 'Doc No', value: 'docNo' },
                                { label: 'KAM Name', value: 'KAM' }]
                        
                            return { ...eachOption, options: updatedOptions }
                        }
                        return eachOption
                    })

                    let relatedSearchConfig = searchConfig?.filter(eachOption => eachOption.name != "dateType" || eachOption.name === "warehouseId")

                    setSearchConfig([...relatedSearchConfig])
                    dispatch(updateSearchOptions([...updatedSearchOptions]))
                }
            }
            else if (searchParams?.dataType === "buyerPOStock"){
                let updatedSearchParams = { ...defaultStockSearchParams, dataType: searchParams.dataType }
                if (updatedSearchParams.fromDate) {
                    delete updateSearchParams.fromDate
                }

                if (updatedSearchParams.toDate) {
                    delete updateSearchParams.toDate
                }

                if (updatedSearchParams.dateType) {
                    delete updateSearchParams.dateType
                }

                dispatch(updateSearchParams(updatedSearchParams))

                if (searchOptions.length != 0) {
                    let relatedSearchOptions = searchOptions?.filter(eachOption => eachOption.name != "dateType")
                    relatedSearchOptions = relatedSearchOptions.filter(eachSearchOpt => eachSearchOpt.name !== "docType" && eachSearchOpt.name != "stockMoveType" && eachSearchOpt.name !== "warehouseId")
                    let updatedSearchOptions = [...relatedSearchOptions]
                    updatedSearchOptions = updatedSearchOptions?.map(eachOption => {
                        if (eachOption?.name === "searchBy") {
                            let relatedSearchByOptions = [
                                { label: 'No Filters', value: 'all' },
                                { label: 'Material Id', value: 'materialId' },
                                { label: 'Material Details', value: 'materialDetails' },
                                { label: 'Doc No', value: 'docNo' }
                            ]
                            return { ...eachOption, options: relatedSearchByOptions }
                        }
                        return eachOption
                    })

                    let relatedSearchConfig = searchConfig?.filter(eachOption => eachOption.name != "dateType")

                    let warehouseSearchConfig = searchConfig?.find(eachOption => eachOption.name === "warehouseId")
                    if (!warehouseSearchConfig) {
                        let relatedCustomSearchConfig = customStockMovesSearchConfig?.find(eachOption => eachOption.name === "warehouseId")
                        if (relatedCustomSearchConfig) {
                            setSearchConfig([...relatedSearchConfig, relatedCustomSearchConfig])
                        }
                    }else {
                        if (relatedSearchConfig.length > 0) {
                            setSearchConfig([...relatedSearchConfig])
                        }
                    }

                    dispatch(updateSearchOptions([...updatedSearchOptions]))
                }
            }
            else if (searchParams?.dataType === "discardedStock"){
                if (searchOptions.length != 0) {

                    let updatedSearchOptions = [...searchOptions]
                    updatedSearchOptions = updatedSearchOptions.filter(eachSearchOpt => eachSearchOpt.name !== "docType" && eachSearchOpt.name != "stockMoveType" && eachSearchOpt.name !== "warehouseId")
                    let dateTypeSearchOption = searchOptions?.find(eachOption => eachOption.name === "dateType")
                    if (!dateTypeSearchOption) {
                        let relatedCustomSearchConfig = customStockMovesSearchConfig?.find(eachOption => eachOption.name === "dateType")
                        if (relatedCustomSearchConfig) {
                            updatedSearchOptions.push(relatedCustomSearchConfig)
                        }
                    }

                    updatedSearchOptions = updatedSearchOptions?.map(eachOption => {
                        if (eachOption?.name === "searchBy") {
                            let relatedSearchByOptions = [
                                { label: 'No Filters', value: 'all' },
                                { label: 'Material Id', value: 'materialId' },
                                { label: 'Material Details', value: 'materialDetails' }
                            ]
                            return { ...eachOption, options: relatedSearchByOptions }
                        }
                        return eachOption
                    })

                    let updatedSearchConfig = [...searchConfig]
                    let relatedSearchConfig = searchConfig?.find(eachOption => eachOption.name === "dateType")
                    if (!relatedSearchConfig) {
                        let relatedCustomSearchConfig = customStockMovesSearchConfig?.find(eachOption => eachOption.name === "dateType")
                        if (relatedCustomSearchConfig) {
                            updatedSearchConfig.push(relatedCustomSearchConfig)
                        }
                    }

                    let warehouseSearchConfig = searchConfig?.find(eachOption => eachOption.name === "warehouseId")
                    if (!warehouseSearchConfig) {
                        let relatedCustomSearchConfig = customStockMovesSearchConfig?.find(eachOption => eachOption.name === "warehouseId")
                        if (relatedCustomSearchConfig) {
                            updatedSearchConfig.push(relatedCustomSearchConfig)
                        }
                    } 

                    let updatedSearchParams = { ...defaultStockSearchParams, dataType: searchParams.dataType }

                    let fromDate = new Date()
                    fromDate.setDate(fromDate.getDate() - 30)
                    fromDate.setHours(0, 0, 0, 0)
                    fromDate = fromDate.toISOString()

                    // let toDate = new Date()
                    // toDate.setDate(toDate.getDate())
                    // toDate.setHours(0, 0, 0, 0)
                    // toDate = toDate.toISOString()

                    updatedSearchParams.fromDate = fromDate
                    // updatedSearchParams.toDate = toDate
                    updatedSearchParams.dateType = 'reportDate'

                    dispatch(updateSearchParams(updatedSearchParams))
                    dispatch(updateSearchOptions([...updatedSearchOptions]))
                    setSearchConfig(updatedSearchConfig)
                }
            }
            else if(searchParams?.dataType === "stockAging"){
                if (searchOptions.length != 0) {

                    let updatedSearchOptions = [...searchOptions]
                    
                    updatedSearchOptions = updatedSearchOptions.filter(eachSearchOpt => eachSearchOpt.name !== "docType" && eachSearchOpt.name != "stockMoveType" && eachSearchOpt.name !== "warehouseId")
                    let dateTypeSearchOption = searchOptions?.find(eachOption => eachOption.name === "dateType")
                    if (!dateTypeSearchOption) {
                        let relatedCustomSearchConfig = customStockMovesSearchConfig?.find(eachOption => eachOption.name === "dateType")
                        if (relatedCustomSearchConfig) {
                            updatedSearchOptions.push(relatedCustomSearchConfig)
                        }
                    }

                    updatedSearchOptions = updatedSearchOptions?.map(eachOption => {
                        if (eachOption?.name === "searchBy") {
                            let relatedSearchByOptions = [
                                { label: 'No Filters', value: 'all' },
                                { label: 'Material Id', value: 'materialId' },
                                { label: 'Material Details', value: 'materialDetails' }
                            ]
                            return { ...eachOption, options: relatedSearchByOptions }
                        }
                        return eachOption
                    })


                    let updatedSearchConfig = [...searchConfig]
                    let relatedSearchConfig = searchConfig?.find(eachOption => eachOption.name === "dateType")
                    if (!relatedSearchConfig) {
                        let relatedCustomSearchConfig = customStockMovesSearchConfig?.find(eachOption => eachOption.name === "dateType")
                        if (relatedCustomSearchConfig) {
                            updatedSearchConfig.push(relatedCustomSearchConfig)
                        }
                    }

                    let warehouseSearchConfig = searchConfig?.find(eachOption => eachOption.name === "warehouseId")
                    if (!warehouseSearchConfig) {
                        let relatedCustomSearchConfig = customStockMovesSearchConfig?.find(eachOption => eachOption.name === "warehouseId")
                        if (relatedCustomSearchConfig) {
                            updatedSearchConfig.push(relatedCustomSearchConfig)
                        }
                    } 

                    let updatedSearchParams = { ...defaultStockSearchParams, dataType: searchParams.dataType }

                    let fromDate = new Date()
                    fromDate = fromDate.toISOString()

                    let minDate = new Date(2024, 5, 17)

                    updatedSearchParams.fromDate = fromDate
                    updatedSearchParams.dateType = 'reportDate'
                    updatedSearchParams.minDate = minDate

                    dispatch(updateSearchParams(updatedSearchParams))
                    dispatch(updateSearchOptions([...updatedSearchOptions]))
                    setSearchConfig(updatedSearchConfig)
                }
            }
            else if (searchParams?.dataType === "rejectedStock"){
                if (searchOptions.length != 0) {

                    let updatedSearchOptions = [...searchOptions]
                    updatedSearchOptions = updatedSearchOptions.filter(eachSearchOpt => eachSearchOpt.name !== "docType" && eachSearchOpt.name != "stockMoveType" && eachSearchOpt.name !== "warehouseId")
                    let dateTypeSearchOption = searchOptions?.find(eachOption => eachOption.name === "dateType")
                    if (!dateTypeSearchOption) {
                        let relatedCustomSearchConfig = customStockMovesSearchConfig?.find(eachOption => eachOption.name === "dateType")
                        if (relatedCustomSearchConfig) {
                            updatedSearchOptions.push(relatedCustomSearchConfig)
                        }
                    }

                    updatedSearchOptions = updatedSearchOptions?.map(eachOption => {
                        if (eachOption?.name === "searchBy") {
                            let relatedSearchByOptions = [
                                { label: 'No Filters', value: 'all' },
                                { label: 'Material Id', value: 'materialId' },
                                { label: 'Material Details', value: 'materialDetails' }
                            ]
                            return { ...eachOption, options: relatedSearchByOptions }
                        }
                        return eachOption
                    })

                    let updatedSearchConfig = [...searchConfig]
                    let relatedSearchConfig = searchConfig?.find(eachOption => eachOption.name === "dateType")
                    if (!relatedSearchConfig) {
                        let relatedCustomSearchConfig = customStockMovesSearchConfig?.find(eachOption => eachOption.name === "dateType")
                        if (relatedCustomSearchConfig) {
                            updatedSearchConfig.push(relatedCustomSearchConfig)
                        }
                    }

                    let warehouseSearchConfig = searchConfig?.find(eachOption => eachOption.name === "warehouseId")
                    if (!warehouseSearchConfig) {
                        let relatedCustomSearchConfig = customStockMovesSearchConfig?.find(eachOption => eachOption.name === "warehouseId")
                        if (relatedCustomSearchConfig) {
                            updatedSearchConfig.push(relatedCustomSearchConfig)
                        }
                    } 

                    let updatedSearchParams = { ...defaultStockSearchParams, dataType: searchParams.dataType }

                    let fromDate = new Date()
                    fromDate.setDate(fromDate.getDate() - 30)
                    fromDate.setHours(0, 0, 0, 0)
                    fromDate = fromDate.toISOString()

                    // let toDate = new Date()
                    // toDate.setDate(toDate.getDate())
                    // toDate.setHours(0, 0, 0, 0)
                    // toDate = toDate.toISOString()

                    updatedSearchParams.fromDate = fromDate
                    // updatedSearchParams.toDate = toDate
                    updatedSearchParams.dateType = 'rejectedDate'

                    dispatch(updateSearchParams(updatedSearchParams))
                    dispatch(updateSearchOptions([...updatedSearchOptions]))
                    setSearchConfig(updatedSearchConfig)
                }
            }
            else if (searchParams?.dataType === "stockConsumption"){
                if (searchOptions.length != 0) {

                    let updatedSearchOptions = [...searchOptions]
                    updatedSearchOptions = updatedSearchOptions.filter(eachSearchOpt => eachSearchOpt.name !== "docType" && eachSearchOpt.name != "stockMoveType" && eachSearchOpt.name !== "warehouseId")
                    let dateTypeSearchOption = searchOptions?.find(eachOption => eachOption.name === "dateType")
                    if (!dateTypeSearchOption) {
                        let relatedCustomSearchConfig = customStockMovesSearchConfig?.find(eachOption => eachOption.name === "dateType")
                        if (relatedCustomSearchConfig) {
                            updatedSearchOptions.push(relatedCustomSearchConfig)
                        }
                    }

                    updatedSearchOptions = updatedSearchOptions?.map(eachOption => {
                        if (eachOption?.name === "searchBy") {
                            let relatedSearchByOptions = [
                                { label: 'No Filters', value: 'all' },
                                { label: 'Material Id', value: 'materialId' },
                                { label: 'Material Details', value: 'materialDetails' }
                            ]
                            return { ...eachOption, options: relatedSearchByOptions }
                        }
                        return eachOption
                    })
                    
                    let updatedSearchConfig = [...searchConfig]
                    let relatedSearchConfig = searchConfig?.find(eachOption => eachOption.name === "dateType")
                    if (!relatedSearchConfig) {
                        let relatedCustomSearchConfig = customStockMovesSearchConfig?.find(eachOption => eachOption.name === "dateType")
                        if (relatedCustomSearchConfig) {
                            updatedSearchConfig.push(relatedCustomSearchConfig)
                        }
                    }

                    let warehouseSearchConfig = searchConfig?.find(eachOption => eachOption.name === "warehouseId")
                    if (!warehouseSearchConfig) {
                        let relatedCustomSearchConfig = customStockMovesSearchConfig?.find(eachOption => eachOption.name === "warehouseId")
                        if (relatedCustomSearchConfig) {
                            updatedSearchConfig.push(relatedCustomSearchConfig)
                        }
                    } 

                    let updatedSearchParams = { ...defaultStockSearchParams, dataType: searchParams.dataType }

                    let fromDate = new Date()
                    fromDate.setDate(fromDate.getDate() - 30)
                    fromDate.setHours(0, 0, 0, 0)
                    fromDate = fromDate.toISOString()

                    let toDate = new Date()
                    toDate.setDate(toDate.getDate())
                    toDate.setHours(0, 0, 0, 0)
                    toDate = toDate.toISOString()

                    updatedSearchParams.fromDate = fromDate
                    updatedSearchParams.toDate = toDate
                    updatedSearchParams.dateType = 'sellerPODate'

                    dispatch(updateSearchParams(updatedSearchParams))
                    dispatch(updateSearchOptions([...updatedSearchOptions]))
                    setSearchConfig(updatedSearchConfig)
                }
            }
        }
    }, [searchParams.dataType])

    useEffect(() => {
        if(searchParams){
            dispatch(updateSearchParams({...searchParams, searchText: ''}))
        }
    }, [searchParams.searchBy])

    useEffect(() => {
        if (props.companyWarehouses?.length > 0) {
            customStockMovesSearchConfig?.map(eachConfig => {
                if (eachConfig?.name === "warehouseId") {
                    if(!eachConfig?.options){
                        const updatedCompanyWarehouses = props.companyWarehouses?.map(eachWarehouse => ({ label: eachWarehouse.warehouseName, value: eachWarehouse.warehouseId }))
                        eachConfig.options = updatedCompanyWarehouses
                        return { ...eachConfig }
                    }
                }
                return eachConfig
            })
        }
    }, [props.companyWarehouses])

    useEffect(() => {
        if (props.stockMoveTypes?.length > 0) {
            customStockMovesSearchConfig?.map(eachConfig => {
                if (eachConfig?.name === "stockMoveType") {
                    if(!eachConfig?.options){
                        const updatedStockMoveTypes = props.stockMoveTypes?.map(eachMoveType => ({ label: eachMoveType.stockMoveName, value: eachMoveType.stockMoveTypeId }))
                        eachConfig.options = updatedStockMoveTypes
                        return { ...eachConfig }
                    }
                }
                return eachConfig
            })
        }
    }, [props.stockMoveTypes])

    const addFilterOption = (srNo) => {

        const relatedConfig = searchConfig.find(eachConfig => eachConfig.srNo === srNo)
        
        // if(relatedConfig.name === "warehouseId"){
        //     const updatedCompanyBranches = props.companyWarehouses?.map(eachWarehouse => ({label:eachWarehouse.warehouseName, value:eachWarehouse.warehouseId}))
        //     relatedConfig.options = updatedCompanyBranches
        // }
        if(searchParams.dataType === "allStockMoves"){
            if (relatedConfig.type === "DATE") {
                let toDate = new Date()
                toDate.setHours(23, 59, 0, 0)
                toDate = toDate.toISOString()
    
                let fromDate = new Date()
                fromDate.setDate(fromDate.getDate() - 30)
                fromDate.setHours(0, 0, 0, 0)
                fromDate = fromDate.toISOString()
    
                const updatedSearchParams = {
                    ...searchParams,
                    fromDate: fromDate,
                    toDate: toDate,
                    dateType: 'moveDate'
                }
                
                dispatch(updateSearchParams(updatedSearchParams))
            }
        }else{
            
            let updatedSearchParams = {...searchParams}
            
            if(updatedSearchParams.fromDate){
                delete updateSearchParams.fromDate
            }
            
            if(updatedSearchParams.toDate){
                delete updateSearchParams.toDate
            }

            if(updatedSearchParams.dateType){
                delete updateSearchParams.dateType
            }

            dispatch(updateSearchParams(updatedSearchParams))
        }
        
        dispatch(updateSearchOptions([...searchOptions, relatedConfig]))

    }

    const removeFilterOption = (srNo) => {
        const relatedSearchOption = searchOptions.find(eachOption => eachOption.srNo === srNo)
        const updatedSearchParams = { ...searchParams }

        if (relatedSearchOption.type === "SEARCH_BY") {
            updatedSearchParams.searchText = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "DATE") {
            updatedSearchParams.fromDate = null
            updatedSearchParams.toDate = null
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "SELECT") {
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "MULTISELECT") {
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }

        const filteredSearchOptions = searchOptions.filter(eachOption => eachOption.srNo !== srNo)
        dispatch(updateSearchOptions([...filteredSearchOptions]))
    }

    const handleSearchOnChange = (event) => {
        let eventValue = event.target.value
        const updatedSearchParams = { ...searchParams, [event.target.name]: event.target.value }

        if (eventValue instanceof Date) {
            if(new Date(searchParams[event.target.name])?.getTime() != eventValue.getTime()){
                updatedSearchParams.range = null
            }

            if (event.target.name === "toDate") {
                eventValue.setHours(23, 59, 0, 0)
            }
            if (event.target.name === "fromDate") {
                eventValue.setHours(0, 0, 0, 0)
            }
            eventValue = eventValue.toISOString()

            updatedSearchParams[event.target.name] = eventValue
        }

        dispatch(updateSearchParams(updatedSearchParams))

        if (event.target.name === 'dataType') {
            dispatch(updateStockReportData([]))
            dispatch(updatePaginationParams({
                pageNo: 0,
                totalRecords: 0,
                recordsPerPage: 10,
                limitedPagination: true,
                getCount: true
            }))
        }

        handleTableColumn(updatedSearchParams)
    }

    const addFilterOptions = []
    searchConfig.map(eachConfig => {
        const optionExists = searchOptions.some(eachOption => {
            if (eachOption.srNo === eachConfig.srNo) {
                return true
            }
        })

        console.log("searchOptions", searchOptions)

        if (!optionExists) {
            addFilterOptions.push({
                label: eachConfig.label,
                command: () => {
                    addFilterOption(eachConfig.srNo)
                }
            })
        }
    })

    const getStockProductName = (row) => {
        let relatedBrand = materialBrands.find(eachBrand => eachBrand.brandId === row?.materialMaster?.brandId)
        let productName = getProductName(relatedBrand?.brandName,row?.materialMaster?.productName, row?.materialMaster?.variantName)
        return productName
    }

    const getStockProductSpecification = (row) => {
        let productSpecification = getProductSpecification(row?.materialMaster?.productSpecification, row?.materialMaster?.UOM,row?.materialMaster?.UOMValue)
        return productSpecification
    }

    const getStockWareHouseName = (row) => {
        const relatedCompanyWarehouse = companyWarehouses.find((each) => each.warehouseId === row?.warehouseId)
        if(relatedCompanyWarehouse?.warehouseName){
            return relatedCompanyWarehouse.warehouseName
        }else{
            return ''
        }
    }

    const getStockStatus = (row) => {
        let relatedStatus = statusList.find(eachStatus => eachStatus.statusId === row.statusId)

        if(relatedStatus?.status){
            return relatedStatus.status
        }else{
            return ''
        }
    }

    const getPrice = (row) => {
        if(row?.price !== null){
            return numberConversion(row.price, numberLocale)
        }
        return ""
    }

    const getStockMoveDocNo = (row) => {
        if(row?.docNo){
            return row?.docNo
        }

        return ''
    }

    const getStockMoveTypeName = (row) => {
        const relatedStockMoveTypes = stockMoveTypes.find((each) => each.stockMoveTypeId === row?.stockMoveTypeId)
        if(relatedStockMoveTypes?.stockMoveName){
            return relatedStockMoveTypes?.stockMoveName
        }

        return ''
    }

    const getBuyerName = (row) => {
        if(row.buyerCompanyName){
            return getCompanyShortName(row.buyerCompanyName)
        }
        
        return ""
    }

    const getSellerName = (row) => {
        if(row.sellerCompanyName){
            return getCompanyShortName(row.sellerCompanyName)
        }

        return ""
    }

    const getStockMoveDate = (row) => {
        if(row?.createdAt){
            let dateString = getDateString(row?.createdAt)
            return dateString
        }
        
        return ""
    }

    const getStockMoveCreatedBy = (row) => {
        if(row.firstName){
            return getFullName(row.firstName, row.lastName)
        }
        
        return ""
    }

    const getStockMoveRemarks = (row) => {
        if(row?.remarks){
            return row?.remarks
        }
        
        return ""
    }

    const getBuyerPODocNo = (row) => {
        if (row.buyerPODocNo) {
            return row.buyerPODocNo
        }

        return ''
    }

    const getSellerPODocNo = (row) => {
        if (row.sellerPODocNo) {
            return row.sellerPODocNo
        }
        return ''
    }

    const getFileName = (name, format) => {
        return `${name}.${format}`
    }

    const getSellerInvoiceNo = (sellerInvoiceNo) => {

        const sellerInvs = _.compact(sellerInvoiceNo)
        if(!sellerInvs.length){
            return ''
        }

        return sellerInvs.join(', ')
    }

    const getGRNDates = (GRNDate) => {
        const date = new Date(GRNDate)

        return getDateString(date)
    }

    const getAge = (Age) => {

        const age = _.compact(Age)
        if(!age.length){
            return ''
        }

        return age.join(', ')
    }

    const getProductDetailsBody = (row) => {
        let relatedBrand = materialBrands.find(eachBrand => eachBrand.brandId === row.brandId)
        let productName = getProductName(relatedBrand?.brandName, row.productName, row.variantName)
        let productSpecification = getProductSpecification(row.productSpecification)
        return `${productName} | ${productSpecification}`
    }

    const getBuyerPOProductBody = (row) => {
        return `${row.product} | ${row.productSpecification}`
    }

    const downloadStockDocData = async (stockDocData) => {

        let stockDocSheetData = []

        for (let i = 0; i < stockDocData.length; i++) {

            const newStockDocObj = {
                'Sr. No.': i + 1,
                'MaterialId': stockDocData[i]['materialId'],
                "Product" : getStockProductName(stockDocData[i]),
                "Specification" : getStockProductSpecification(stockDocData[i]),
                "Doc No" : stockDocData[i]['docNo'],
                "Doc Qty" : stockDocData[i]['docQty'],
                "Pending Qty" : stockDocData[i]['pendingQty'],
                "InProcess Qty" : stockDocData[i]['inProcessQty'],
                "Invoiced Qty" : stockDocData[i]['invoicedQty'],
                "Adjusted Qty" : stockDocData[i]['adjustedQty'],
                "CNDN Qty" : stockDocData[i]['CNDNQty'],
            }

            stockDocSheetData.push(newStockDocObj)
        }

        return stockDocSheetData
    }

    const downloadStockWarehouseData = async (stockWarehouseData) => {

        let stockWarehouseSheetData = []

        stockWarehouseData.forEach((eachStockWarehouseData, index) => {
            const newStockWarehouseObj = {
                'Sr. No.': index + 1,
                'MaterialId': eachStockWarehouseData.materialId,
                "Warehouse Name": eachStockWarehouseData.warehouseName,
                "Product": eachStockWarehouseData.product,
                "Specification": eachStockWarehouseData.productSpecification,
                "In Stock": eachStockWarehouseData.inStock,
                "Out Stock": eachStockWarehouseData.outStock,
                "Dispatched Stock": eachStockWarehouseData.dispatchedStock,
                "Smart Stock": eachStockWarehouseData.smartStock,
                "Rejected Stock": eachStockWarehouseData.rejectedStock,
                "Discarded Stock": eachStockWarehouseData.discardedStock,
                // "Last Smart Price": eachStockWarehouseData.lastSmartPrice,
                // "Last Rejected Price": eachStockWarehouseData.lastRejectedPrice,
                // "Smart Stock Price": eachStockWarehouseData.smartStockPrice,
                "OutStock Value": eachStockWarehouseData.outStockValue,
                "SmartStock Value": eachStockWarehouseData.smartStockValue
            }

            stockWarehouseSheetData.push(newStockWarehouseObj)
        })

        return stockWarehouseSheetData
    }

    const downloadStockMoveData = async (stockMoveData) => {

        let stockMoveSheetData = []

        for (let i = 0; i < stockMoveData.length; i++) {
            console.log("stockMoveData", stockMoveData[i])

            const newStockMoveObj = {
                "Sr. No.": i + 1,
                "StockMove Id": stockMoveData[i]['stockMoveId'],
                "MaterialId": stockMoveData[i]['materialId'],
                "Warehouse Name": getStockWareHouseName(stockMoveData[i]),
                "Product": getProductDetailsBody(stockMoveData[i]),
                "Move Qty": stockMoveData[i]['moveQty'],
                "Price": getPrice(stockMoveData[i]),
                "Move Date": getStockMoveDate(stockMoveData[i]),
                "DocNo" : getStockMoveDocNo(stockMoveData[i]),
                "StockMove Type": getStockMoveTypeName(stockMoveData[i]),
                "Created By": getStockMoveCreatedBy(stockMoveData[i]),
                "Buyer Name" : getBuyerName(stockMoveData[i]),
                "BuyerPO DocNo" : getBuyerPODocNo(stockMoveData[i]),
                "Seller Name" : getSellerName(stockMoveData[i]),
                "SellerPO DocNo" : getSellerPODocNo(stockMoveData[i]),
                "Remarks": getStockMoveRemarks(stockMoveData[i])
            }

            console.log("newStockMoveObj", newStockMoveObj)

            stockMoveSheetData.push(newStockMoveObj)
        }

        return stockMoveSheetData
    }

    const downloadStockMovementData = async (stockEODWarehouseData) => {
        const stockEODWarehouseSheetData = []

        let count = 1
        stockEODWarehouseData.forEach(eachMovement => {
            const newStockWarehouseObj = {
                'Sr. No.': count,
                'MaterialId': eachMovement.materialId,
                "Warehouse Name": eachMovement.warehouseName,
                "Product": eachMovement.product,
                "Opening Qty": eachMovement.openingQty,
                "Received Qty": eachMovement.receivedQty,
                'Adjusted Qty': eachMovement.adjustedQty,
                'Recieved By CNQty': eachMovement.recievedByCNQty,
                "Dispatched Qty": eachMovement.dispatchedQty,
                "Closing Qty": eachMovement.closingQty,
                "Opening Amount": eachMovement.openingAmount,
                'Received Amount': eachMovement.receivedAmount,
                "Dispatched Amount": eachMovement.dispatchedAmount,
                'Closing Amount': eachMovement.closingAmount
            }
            count += 1

            stockEODWarehouseSheetData.push(newStockWarehouseObj)
        })

        return stockEODWarehouseSheetData
    }

    const downloadStockVarianceData = async (stockVarianceData) => {
        const stockVarianceSheetData = []

        let count = 1
        stockVarianceData.forEach(eachStockVariance => {
            const newStocVarianceObj = {
                'Sr. No.': count,
                'MaterialId': eachStockVariance.materialId,
                "Product": eachStockVariance.product,
                "Doc No": eachStockVariance.docNo,
                "KAM": eachStockVariance.KAMName,
                "Branch": eachStockVariance.city,
                "purchased Qty": eachStockVariance.purchasedQty,
                "Price": eachStockVariance.price,
                "Value": eachStockVariance.value,
                "Dispatched Qty": eachStockVariance.dispatchedQty,
                "+ve Adjusted Qty": eachStockVariance['+ve AdjustedQty'],
                "-ve Adjusted Qty": eachStockVariance['-ve AdjustedQty'],
                "Variance": round(eachStockVariance.variance)
            }
            count += 1

            stockVarianceSheetData.push(newStocVarianceObj)
        })

        return stockVarianceSheetData
    }

    const downloadbuyerPOStockData = async (buyerPOStockData) => {
        const buyerPOStockSheetData = []

        let count = 1
        buyerPOStockData.forEach(eachBuyerPOStock => {
            const newBuyerPOStockObj = {
                'Sr. No.': count,
                "Buyer Doc": eachBuyerPOStock['Buyer Doc'],
                "Client PO Num": eachBuyerPOStock.clientPONum,
                "Client Name": eachBuyerPOStock.clientName,
                'MaterialId': eachBuyerPOStock.materialId,
                "Product": getBuyerPOProductBody(eachBuyerPOStock),
                "Warehouse Name": eachBuyerPOStock.warehouseName,
                "Company ERP Code": eachBuyerPOStock.companyERPCode,
                "Seller Doc": eachBuyerPOStock['Seller Doc'],
                "Seller PO Qty": eachBuyerPOStock['Seller PO Qty'],
                "KAM": eachBuyerPOStock.KAMName,
                "In Stock": eachBuyerPOStock.inStock,
                "Smart Stock": eachBuyerPOStock.smartStock,
                "Smart Stock Price": eachBuyerPOStock.smartStockPrice,
                "Out Stock": eachBuyerPOStock.outStock,
                "Out Stock Price": eachBuyerPOStock.outStockPrice,
                "Rejected Stock": eachBuyerPOStock.rejectedStock,
                "Rejected Price": eachBuyerPOStock.rejectedPrice,
                "Dispatched Stock": eachBuyerPOStock.dispatchedStock,
                "Discarded Stock": eachBuyerPOStock.discardedStock
            }
            count += 1

            buyerPOStockSheetData.push(newBuyerPOStockObj)
        })

        return buyerPOStockSheetData
    }

    const downloadDiscardedStockData = async (discardedStockData) => {
        const discardStockSheetData = []

        let count = 1
        discardedStockData.forEach(eachDiscardedStock => {
            const newDiscardedStock = {
                'Sr. No.': count,
                'MaterialId': eachDiscardedStock.materialId,
                "Warehouse Name": eachDiscardedStock.warehouseName,
                "Product": eachDiscardedStock.product,
                'Product Specification': eachDiscardedStock.productSpecification,
                "Credit Note No": eachDiscardedStock['CreditNote No'],
                "Discarded Qty": eachDiscardedStock.discardedQty,
                "Discarded By": eachDiscardedStock.DiscardedBy,
                "Discarded At": eachDiscardedStock.discardedAt,
                "Discarded Reason": eachDiscardedStock.discardedReason,
                "Rejected By": eachDiscardedStock.RejectedBy,
                "Rejected At": eachDiscardedStock.rejectedAt,
                "Rejection Reason": eachDiscardedStock.rejectionReason
            }
            count += 1

            discardStockSheetData.push(newDiscardedStock)
        })

        return discardStockSheetData
    }

    const downloadStockAgingReportData = async (stockAgingData) => {
        const stockAgingReportSheetData = []

        let count = 1
        stockAgingData.forEach(eachStock => {
            const newStock = {
                'Sr. No.': count,
                'MaterialId': eachStock.materialId,
                "Warehouse Name": eachStock.warehouseName,
                "Product": eachStock.product,
                'Product Specification': eachStock.productSpecification,
                "Category": eachStock["Product Industry"],
                'Buyer PO': eachStock['BuyerPODoc'],
                'Client Name': eachStock['clientName'],
                "Seller PO": eachStock.sellerPODocNo,
                "SellerPO Date": eachStock.sellerPODate,
                'Seller Invoice No': eachStock.sellerInvoiceNo,
                "KAM Name": eachStock.KAMName,
                "Type": eachStock['Type'],
                "Move Qty": eachStock['Qty'],
                "UOM": eachStock["UOM"],
                "Price": eachStock.price,
                "Stock Value": round(eachStock["StockValue"]),
                "GRNDate": getGRNDates(eachStock.GRNDate),
                "Age": eachStock['Age'],
                "Stock Aging Bucket": eachStock["Stock Ageing Bucket"]
            }
            count += 1

            stockAgingReportSheetData.push(newStock)
        })

        return stockAgingReportSheetData
    }

    const downloadRejectedStockReportData = async (rejectedStockData) => {
        const rejectedStockSheetData = []

        rejectedStockData.forEach((eachStock, index) => {
            const newStock = {
                'Sr. No.': index+1,
                'MaterialId': eachStock.materialId,
                "Warehouse Name": eachStock.warehouseName,
                "Product": eachStock.product,
                'Product Specification': eachStock.productSpecification,
                "Buyer PO": eachStock.buyerDoc,
                "Seller PO": eachStock.sellerDoc,
                "Buyer Invoice No": eachStock.buyerInvoiceNo,
                "Credit Note No": eachStock['CreditNote No'],
                "Client name": eachStock.clientName,
                "Client Hold Qty": eachStock.clientHoldQty,
                "Client Hold Value": eachStock.clientHoldValue,
                "Rejected qty": eachStock.rejectedQty,
                "Price": eachStock.price,
                "Rejected Value": eachStock.rejectedValue,
                "Rejected at": eachStock.rejectedAt,
                "Rejected by": eachStock.RejectedBy,
                "Rejection reason": eachStock.rejectionReason
            }

            rejectedStockSheetData.push(newStock)
        })

        return rejectedStockSheetData
    }

    const downloadStockConsumptionReportData = async (stockConsumptionData) => {
        const stockConsumptionSheetData = []

        stockConsumptionData.forEach((eachStock, index) => {
            const newStock = {
                'Sr. No.': index+1,
                'MaterialId': eachStock.materialId,
                "Warehouse Name": eachStock.warehouseName,
                "Product": eachStock.product,
                'Product Specification': eachStock.productSpecification,
                "PM Branch": eachStock.PMBranch,
                "Stock Seller PO": eachStock.stockSellerPO,
                "Seller PO Date": eachStock.sellerPODate,
                "KAM Name": eachStock.KAMName,
                "GRN Date": eachStock.GRNDate,
                "Seller Name": eachStock.SellerName,
                "useFromStock Seller PO": eachStock.useFromStockSellerPO,
                "Consumed Qty": eachStock.consumedQty,
                "Buyer Doc No": eachStock.buyerDocNo,
                "Client Name": eachStock.ClientName,
                "Invoice Date": eachStock.InvoiceDate
            }

            stockConsumptionSheetData.push(newStock)
        })

        return stockConsumptionSheetData
    }

    const downloadData = async (dataFormat) => {
        let response = []
        setIsLoading(true)
        if (searchParams?.dataType === "stockWarehouse") {
            response = await searchStockWarehouse({ ...searchParams, paginator: false })
        } 
        else if(searchParams?.dataType === "stockDoc"){
            response = await searchStockDoc({ ...searchParams, paginator: false })
        } 
        else if(searchParams?.dataType === "allStockMoves"){
            response = await searchStockMoves({ ...searchParams, paginator: false })
        }
        else if(searchParams?.dataType === "stockMovement"){
            response = await getStockMovementReport({ ...searchParams, paginator: false })
        }
        else if(searchParams?.dataType === "stockVariance"){
            response = await getStockVarianceReport({ ...searchParams, paginator: false })
        }
        else if(searchParams?.dataType === "buyerPOStock"){
            response = await getBuyerPOStockReport({ ...searchParams, paginator: false })
        }
        else if(searchParams?.dataType === "discardedStock"){
            response = await getDiscardedStockReport({ ...searchParams, paginator: false })
        }
        else if(searchParams?.dataType === "stockAging"){
            response = await getStockAgingReport({ ...searchParams, paginator: false })
        }
        else if(searchParams?.dataType === "rejectedStock"){
            response = await getRejectedStockReport({ ...searchParams, paginator: false })
        }
        else if(searchParams?.dataType === "stockConsumption"){
            response = await getStockConsumptionStockReport({ ...searchParams, paginator: false })
        }
        setIsLoading(false)

        if (response.failure) {
            return dispatch(raiseToast({ severity: 'error', detail: response.data.error, life: 3000 }))
        }

        if(response?.data?.data?.length === 0){
            return dispatch(raiseToast({severity:'warn', detail: "No data found", life: 3000}))
        }

        const responseData = response?.data?.data

        let downloadSheetData = []

        if (searchParams?.dataType === "stockWarehouse") {
            downloadSheetData = await downloadStockWarehouseData(responseData)
        } else if(searchParams?.dataType === "stockDoc"){
            downloadSheetData = await downloadStockDocData(responseData)
        } else if(searchParams?.dataType === "allStockMoves"){
            downloadSheetData = await downloadStockMoveData(responseData)
        }
        else if(searchParams?.dataType === "stockMovement"){
            downloadSheetData = await downloadStockMovementData(responseData)
        }
        else if(searchParams?.dataType === "stockVariance"){
            downloadSheetData = await downloadStockVarianceData(responseData)
        }
        else if(searchParams?.dataType === "buyerPOStock"){
            downloadSheetData = await downloadbuyerPOStockData(responseData)
        }
        else if(searchParams?.dataType === "discardedStock"){
            downloadSheetData = await downloadDiscardedStockData(responseData)
        }
        else if(searchParams?.dataType === "stockAging"){
            downloadSheetData = await downloadStockAgingReportData(responseData)
        }
        else if(searchParams?.dataType === "rejectedStock"){
            downloadSheetData = await downloadRejectedStockReportData(responseData)
        }
        else if(searchParams?.dataType === "stockConsumption"){
            downloadSheetData = await downloadStockConsumptionReportData(responseData)
        }
        
        if (dataFormat === "CSV") {
            let csv = Papa.unparse(downloadSheetData, { download: true })
            let blob = new Blob([csv])
            let a = window.document.createElement("a")
            a.href = window.URL.createObjectURL(blob, { type: "text/plain" })
            a.download = getFileName(searchParams?.dataType, 'csv')
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
        }
        if (dataFormat === "XLSX") {
            const ws = utils.json_to_sheet(downloadSheetData)
            const wb = utils.book_new()
            utils.book_append_sheet(wb, ws, 'Sheet1')
            const blob = new Blob([write(wb, { bookType: 'xlsx', type: 'array' })])
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = getFileName(searchParams?.dataType, 'xlsx')
            a.click()
            URL.revokeObjectURL(url)
        }
    }

    const downloadFormatOptions = [
        {
            label: "XLSX",
            command: () => downloadData("XLSX")
        },
        {
            label: "CSV",
            command: () => downloadData("CSV")
        }
    ]
    
    const handleSearch = () => {
        if (searchParams.searchBy !== "all") {
            if (searchParams.searchText === "") {
                const errorMsgText = searchParams.searchBy.replace(/(_|-)/g, ' ')
                    .trim()
                    .replace(/\w\S*/g, function (str) {
                        return str.charAt(0).toUpperCase() + str.substr(1)
                    })
                    .replace(/([a-z])([A-Z])/g, '$1 $2')
                    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
                return dispatch(raiseToast({ severity: 'error', summary: errorMsgText, detail: "Please enter search text", life: 3000 }))
            }
        }

        if(searchParams.dataType === "stockDoc" && searchParams.docType.length === 0){
            return dispatch(raiseToast({ severity: 'error', detail: "Please select document type!", life: 3000 }))
        }

        if(searchParams.dataType === "allStockMoves"){
            if(!searchParams.stockMoveType || searchParams?.stockMoveType?.length === 0){
                return dispatch(raiseToast({ severity: 'error', detail: "Please select stock moves types!", life: 3000 }))
            }
        }

        let updatedParams = { ...paginationParams, ...searchParams, pageNo: 0, paginator: true }
        if(searchParams.dataType === "allStockMoves"){
            updatedParams = {...updatedParams, paginator: true}
        }
        
        dispatch(fetchStockReport(updatedParams))
    }

    const handleOnEnter = (e) => {
        if(e.key === "Enter"){
            handleSearch()
        }
    }

    console.log("searchOptions", searchOptions)

    return (
        <div className="p-card" style={{ border: "1px solid #e9ecef" }} onKeyDown={handleOnEnter}>
            <div className="grid">
                {
                    searchOptions.map(eachOption => {
                        if (eachOption.type === "SELECT") {
                            return (
                                <>
                                    {!eachOption.static ?
                                        <div className="flex justify-content-end p-0 col-12 remove-opt">
                                            <i className="pi pi-minus-circle remove-opt-icon" onClick={() => removeFilterOption(eachOption.srNo)} ></i>
                                        </div>
                                        :
                                        null
                                    }
                                    {eachOption.multiSelectOptions ?
                                        <div className="flex flex-column col-12">
                                            <MultiSelectField name={eachOption.name} options={eachOption.options} labelText={eachOption.label} value={searchParams[eachOption.name]} onChange={handleSearchOnChange} />
                                        </div>
                                        :
                                        <div className="flex flex-column col-12">
                                            <DropDownField name={eachOption.name} options={eachOption.options} labelText={eachOption.label} value={searchParams[eachOption.name]} onChange={handleSearchOnChange} />
                                        </div>
                                    }
                                </>
                            )
                        }
                        if (eachOption.type === "SEARCH_BY") {
                            return (
                                <>
                                    {!eachOption.static ?
                                        <div className="flex justify-content-end p-0 col-12 remove-opt">
                                            <i className="pi pi-minus-circle remove-opt-icon" onClick={() => removeFilterOption(eachOption.srNo)}></i>
                                        </div>
                                        :
                                        null
                                    }
                                    <div className="flex flex-column col-12 compny-serch-by-dropdown">
                                        <DropDownField name="searchBy" labelText="Search By" options={eachOption.options} value={searchParams["searchBy"]} onChange={handleSearchOnChange} />
                                    </div>
                                    {searchParams.searchBy !== "all" ?
                                        <div className="flex flex-column col-12">
                                            <TextInputField name="searchText" labelText="Search Text" value={searchParams["searchText"]} onChange={handleSearchOnChange} onKeyDown={handleOnEnter} />
                                        </div> : null
                                    }
                                </>
                            )
                        }
                        if (eachOption.type === "DATE") {
                            const relatedOptions = eachOption.options.filter(eachOpt => eachOpt.dataType === searchParams.dataType)
                            return (
                                <>
                                    {!eachOption.static ?
                                        <div className="flex justify-content-end p-0 col-12 remove-opt">
                                            <i className="pi pi-minus-circle remove-opt-icon" onClick={() => removeFilterOption(eachOption.srNo)} ></i>
                                        </div>
                                        :
                                        null
                                    }
                                    <div className="flex flex-column col-12">
                                        <DropDownField name="dateType" labelText="Date Type" options={relatedOptions} value={searchParams["dateType"]} onChange={handleSearchOnChange} />
                                    </div>
                                    <div className="flex flex-column col w-12">
                                        <CalendarField name="fromDate" labelText="From Date" value={searchParams["fromDate"] && new Date(searchParams["fromDate"])} onChange={handleSearchOnChange} {...searchParams['minDate'] && {minDate: searchParams['minDate']}} maxDate={maxDateCondForCalender} />
                                    </div>
                                    {searchParams["toDate"] && <div className="flex flex-column col-12 w-12">
                                        <CalendarField name="toDate" labelText="To Date" value={searchParams["toDate"] && new Date(searchParams["toDate"])} maxDate={maxDateCondForCalender} onChange={handleSearchOnChange} />
                                    </div>}
                                </>
                            )
                        }
                    })
                }
                <div className="flex col-12 justify-content-end">
                    <Menu model={addFilterOptions} popup ref={filterMenu} />
                    <Button icon="pi pi-filter" className="h-2rem p-2 action-btn hover:surface-300 hover:text-700" label="Add Filter" onClick={(e) => filterMenu.current.toggle(e)} />
                </div>

                <div className="flex flex-column col-12 mb-0 pb-0">
                    <div className="grid">
                        <div className="flex col-12 mb-0 gap-1 pb-0">
                            <Button label="Search" style={{ flexGrow: 1 }} className="py-2" onClick={handleSearch} disabled={isPageLoading} />
                            <Menu model={downloadFormatOptions} popup ref={downloadMenu} />
                            <Button icon="pi pi-download" className="py-2 px-3" onClick={(e) => downloadMenu.current.toggle(e)} disabled={isPageLoading}  permissions={[254]}/>
                        </div>
                    </div>
                </div>
            </div>
            <Loading visible={isLoading} />
        </div>
    )
}

export default Search;